import {useCallback, useEffect, useRef, useState} from "react";
import {$Dialog, $FormikProps, configService, overlayEmitter, PowellProvider} from "@api";
import {FormContainer} from "@components/FormContainer";
import {Button} from "@components/Button";
import {ButtonOnClickAsyncEvent, DialogFormProps} from "@models";
import {InputText} from "@components/InputText";
import {AutoComplete} from "@components/AutoComplete";
import {SelectButton} from "@components/SelectButton";
import {Checkbox} from "@components/Checkbox";
import {Chips} from "@components/Chips";
import {Dropdown} from "@components/Dropdown";
import {Editor} from "@components/Editor";
import {InputMask} from "@components/InputMask";
import {InputNumber} from "@components/InputNumber";
import {InputOtp} from "@components/InputOtp";
import {InputPassword} from "@components/InputPassword";
import {InputSwitch} from "@components/InputSwitch";
import {InputTextarea} from "@components/InputTextarea";
import {Knob} from "@components/Knob";
import {ListBox} from "@components/ListBox";
import {Mention} from "@components/Mention";
import {MultiStateCheckbox} from "@components/MultiStateCheckbox";
import {RadioGroup} from "@components/RadioGroup";
import {Rating} from "@components/Rating";
import {Slider} from "@components/Slider";
import {ToggleButton} from "@components/ToggleButton";
import {TreeSelect} from "@components/TreeSelect";
import {TriStateCheckbox} from "@components/TriStateCheckbox";

export const DialogForm = (props: DialogFormProps) => {
  const {props: componentProps, config} = props;
  const [visible, setVisible] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const isRendered = useRef(false);
  const powellConfig = useRef(configService.get());
  const showDialog = useCallback(() => setVisible(true), []);

  const onHide = useCallback(() => {
    setVisible(false);
    overlayEmitter.off('dialogFormOpen', showDialog);
    overlayEmitter.off('dialogFormClose', () => {
    });
    componentProps.onHide();
  }, [])

  const onSubmit = async (formik: $FormikProps<any>, {event, loadingCallback}: ButtonOnClickAsyncEvent) => {
    event.preventDefault();

    const finalizeSubmit = (hideDialog: boolean) => {
      loadingCallback();
      setDisableReject(false);
      if (hideDialog) {
        onHide();
      }
    };

    const {validateForm, handleSubmit, values, setTouched, touched} = formik;
    const newTouched = config.map(c => c.field).reduce((prev, curr) => ({...prev, [curr]: true}), {});
    await setTouched({...touched, ...newTouched}, true);
    const errors = await validateForm();
    if (Object.keys(errors).length !== 0) {
      loadingCallback();
      return;
    }
    setDisableReject(true);
    handleSubmit();
    overlayEmitter.emit('dialogFormClose', {finalizeSubmit, values});
  }

  useEffect(() => {
    if (isRendered.current) {
      return
    }
    isRendered.current = true;
    overlayEmitter.on('dialogFormOpen', showDialog);
  }, []);

  return (
      <PowellProvider config={powellConfig.current}>
        <$Dialog onHide={onHide} visible={visible}>
          <FormContainer
              validationSchema={componentProps.validationSchema}
              initialValues={componentProps.initialValues ?? {}}
              onSubmit={() => {
              }}>
            {
              (formik) => (
                  <>
                    {
                      config.map((item) => {
                        switch (item.component) {
                          case 'auto-complete':
                            return <AutoComplete key={item.field} name={item.field} {...item} />;
                          case 'button':
                            return <Button key={item.field} name={item.field} {...item} />;
                          case 'cascade-select':
                            return <SelectButton key={item.field} name={item.field} {...item} />;
                          case 'checkbox':
                            return <Checkbox key={item.field} name={item.field} {...item} />;
                          case 'chips':
                            return <Chips key={item.field} name={item.field} {...item} />;
                          case 'dropdown':
                            return <Dropdown key={item.field} name={item.field} {...item} />;
                          case 'editor':
                            return <Editor key={item.field} name={item.field} {...item} />;
                          case 'input-mask':
                            return <InputMask key={item.field} name={item.field} {...item} />;
                          case 'input-number':
                            return <InputNumber key={item.field} name={item.field} {...item} />;
                          case 'input-otp':
                            return <InputOtp key={item.field} name={item.field} {...item} />;
                          case 'input-password':
                            return <InputPassword key={item.field} name={item.field} {...item} />;
                          case 'input-switch':
                            return <InputSwitch key={item.field} name={item.field} {...item} />;
                          case 'input-text':
                            return <InputText key={item.field} name={item.field} {...item} />;
                          case 'input-textarea':
                            return <InputTextarea key={item.field} name={item.field} {...item} />;
                          case 'knob':
                            return <Knob key={item.field} name={item.field} {...item} />;
                          case 'list-box':
                            return <ListBox key={item.field} name={item.field} {...item} />;
                          case 'mention':
                            return <Mention key={item.field} name={item.field} {...item} />;
                          case 'multi-select':
                            return <Mention key={item.field} name={item.field} {...item} />;
                          case 'multi-state-checkbox':
                            return <MultiStateCheckbox key={item.field} name={item.field} {...item} />;
                          case 'radio-group':
                            return <RadioGroup key={item.field} name={item.field} {...item} />;
                          case 'rating':
                            return <Rating key={item.field} name={item.field} {...item} />;
                          case 'select-button':
                            return <SelectButton key={item.field} name={item.field} {...item} />;
                          case 'slider':
                            return <Slider key={item.field} name={item.field} {...item} />;
                          case 'toggle-button':
                            return <ToggleButton key={item.field} name={item.field} {...item} />;
                          case 'tree-select':
                            return <TreeSelect key={item.field} name={item.field} {...item} />;
                          case 'tri-state-checkbox':
                            return <TriStateCheckbox key={item.field} name={item.field} {...item} />;
                          default:
                            return null;
                        }
                      })
                    }
                    <Button async type="submit" onClickAsync={(event) => onSubmit(formik, event)} label="ثبت"/>
                  </>
              )
            }
          </FormContainer>
        </$Dialog>
      </PowellProvider>
  );
}
